<template>
  <div class="bg-white h-auto px-5 rounded-lg">
    <div
      class="flex flex-col lg:flex-row md:items-center lg:gap-2 border-b-[1.5px] border-primary-purple-100 py-5"
    >
      <div class="flex items-center w-full justify-between">
        <Heading :heading="graphHeading" heading-size="text-[18px]" heading-padding="py-0" />
        <div
          v-permission="EXPENSE_FILTERS_PERMISSION"
          class="xl:h-11 md:h-10 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border border-primary-gray-300 rounded-lg px-2"
        >
          <icon icon="filter" height="20" width="20" color="primary-gray-700" />
          <span class="font-inter font-semibold text-primary-gray-700 text-sm hidden sm:block">
            Filters
          </span>
        </div>
      </div>
      <div
        class="w-full lg:w-fit flex flex-wrap-reverse sm:flex-nowrap gap-2 items-end justify-end"
      >
        <UiFilterBox
          v-model="selectedCampus"
          :options="campusOptions"
          :is-switch-box="true"
          :is-disabled="isCampusDisabled"
          label="title"
          reduce="id"
        />
        <CalendarActionsWrapper
          :is-future-date-disabled="true"
          :calendar-date="selectedDate"
          filter-view="Month-range"
          custom-class="right-0 before:right-12"
          @selectDate="onDateChange"
        />
      </div>
    </div>
    <div class="overflow-auto">
      <GraphView :chart-data="graphData" :enable-datalabels="enableDatalabels" class="min-w-7" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'

import Heading from '@/src/components/Heading.vue'
import GraphView from '@src/router/views/graph/graphView.vue'
import CalendarActionsWrapper from '@src/components/Calendar/CalendarActionsWrapper.vue'
import UiFilterBox from '@/src/components/UiElements/UiFilterBox.vue'
import icon from '@src/components/icons/icon.vue'
import { generateRandomArray } from '@utils/generalUtil'
import { useStore } from 'vuex'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import {
  generateDayNames,
  generateFormattedDatesArray,
  getSelectedMonthNames,
} from '@src/utils/moment.util.js'
import { ENV } from '@src/constants/general-constants.js'

const currentClassScope = computed(() => store.state.layout.currentClassScope)
const currentSectionScope = computed(() => store.state.layout.currentSectionScope)
const selectedClass = ref('')
const classOptions = ref([])
const isClassDisabled = computed(() => !!currentClassScope.value)
const isCampusDisabled = computed(() => !!currentCampusScope.value)
const selectedCampus = ref('')
const currentCampusScope = computed(() => store.state.layout.currentCampusScope)
const store = useStore()
const graphHeading = 'Expense'
const enableDatalabels = true
const monthRange = ref(3)
const selectedDate = ref({
  monthRange: monthRange,
  end: new Date(),
  start: (() => {
    const start = new Date()
    start.setMonth(start.getMonth() - (monthRange.value - 1))
    return start
  })(),
})
const campusOptions = computed(() => store.state.layout.campuses)
const EXPENSE_FILTERS_PERMISSION = {
  env: [ENV.DEV],
}

const onDateChange = (date) => {
  selectedDate.value = date
  generateExpenseGraphStats()
}

const generateExpenseGraphStats = async () => {
  if (currentCampusScope.value) selectedCampus.value = currentCampusScope.value.id
  if (currentClassScope.value) selectedClass.value = currentClassScope.value.id
  const monthName = getSelectedMonthNames(selectedDate.value)
  const newdatasets = [
    {
      label: monthName[0],
      backgroundColor: '#1570EF',
      data: generateRandomArray(7, 500, 800),
    },
    {
      label: monthName[1],
      backgroundColor: '#7F56D9',
      data: generateRandomArray(7, 500, 800),
    },
    {
      label: monthName[2],
      backgroundColor: '#FDB022',
      data: generateRandomArray(7, 500, 800),
    },
  ]
  const newLabels = ['Exam Fee', 'Salaries', 'Book', 'Food', 'Picnic', 'Class Fee', 'Others']
  graphData.value.data.labels = newLabels
  graphData.value.data.datasets = newdatasets
}
onMounted(() => {
  generateExpenseGraphStats()
})
watch([currentCampusScope, currentClassScope, currentSectionScope], () => {
  generateExpenseGraphStats()
})
watch([selectedCampus], () => {
  fetchClassList().then(() => {
    generateExpenseGraphStats()
  })
})

const fetchClassList = async () => {
  let query = {
    $where: buildWhereQuery(FILTER_KEYS.EQUAL, 'campus_id', selectedCampus.value),
  }

  try {
    const response = await getClasses(query)
    if (response) {
      classOptions.value = response
    }
  } catch (error) {}
}

const graphData = ref({
  type: 'bar',

  data: {
    labels: [],
    datasets: [],
  },
  options: {
    plugins: {
      tooltip: { backgroundColor: 'white', titleColor: 'black', bodyColor: '#667085' },
      legend: {
        display: true,
        align: 'end',
        labels: {
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          padding: 13,
          font: {
            size: 14,
          },
          textAlign: 'center',
        },
      },
    },

    responsive: true,
    maintainAspectRatio: false,
    barPercentage: 0.6,
    categoryPercentage: 0.2,
    aspectRatio: 100 / 80,
    borderRadius: {
      topLeft: 10,
      topRight: 10,
      bottomLeft: 0,
      bottomRight: 0,
    },
    borderSkipped: false,
    interaction: {
      mode: 'index',
      intersect: false,
      axis: 'x',
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        border: {
          display: false,
        },
        ticks: {
          stepSize: 200,
        },
        beginAtZero: true,
      },
    },
  },
})
const getClasses = (payload) => store.dispatch('classes/getUserClasses', payload)
</script>
